import { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Button,
  Chip,
  Tooltip,
} from "@mui/material";
import {
  Visibility,
  Headphones,
  PersonAdd,
  CreateNewFolder,
  MoreVert,
  CheckCircle,
  Cancel,
} from "@mui/icons-material";
import SearchBar from "../../Subcomponents/Search";
import CreateUserModal from "./CreateUserModal";
import CreateGroupModal from "./CreateGroupModal";
import { getAllSalesManagers } from "../../Services/SmartFloApis";
import AddLeadsModal from "./AddLeadsModal";
import ImportLeadsExcelModal from "./ImportLeadsExcelModal";

// Mock Data
const salesManagers = [
  {
    id: 1,
    name: "John Doe",
    email: "johndoe@example.com",
    extension: "101",
    status: "active",
    leadsCount: 10,
  },
  {
    id: 2,
    name: "Jane Smith",
    email: "janesmith@example.com",
    extension: "102",
    status: "inactive",
    leadsCount: 5,
  },
  // Add more sales managers here
];

const smAnalytics = [
  { salesManagerId: 1, leadHandlingPct: 85 },
  { salesManagerId: 2, leadHandlingPct: 45 },
  // Add more analytics data here
];

const leads = [
  { id: 1, salesManagerId: 1, project: "Project A", unit: "Unit-101" },
  { id: 2, salesManagerId: 1, project: "Project B", unit: "Unit-102" },
  { id: 3, salesManagerId: 2, project: "Project A", unit: "Unit-103" },
  // Add more leads here
];

// Derive unique projects and unit types from leads
const allProjects = [...new Set(leads.map((l) => l.project))];
const allUnitTypes = [
  ...new Set(leads.map((l) => l.unit.split("-")[1] || l.unit)),
];

export default function SmartFloManagerList({ onViewLeads, onViewRecordings }) {
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [smFilter, setSmFilter] = useState("all");
  const [projectFilter, setProjectFilter] = useState("all");
  const [unitFilter, setUnitFilter] = useState("all");
  const [sortBy, setSortBy] = useState("name");
  const [managers, setManagers] = useState([]);
  const [createUserOpen, setCreateUserOpen] = useState(false);
  const [createGroupOpen, setCreateGroupOpen] = useState(false);
  const [addLeadsOpen, setAddLeadsOpen] = useState(false);
  const [importExcelOpen, setImportExcelOpen] = useState(false);
  const [selectedManager, setSelectedManager] = useState(null);
  const [toast, setToast] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchManagers = async () => {
      const res = await getAllSalesManagers();

      console.log("🚀 API RESPONSE IN COMPONENT:", res);

      if (res.success) {
        console.log("📦 FINAL DATA:", res.data);

        const formatted = res.data.map((user) => ({
          id: user.userId,
          name: user.name,
          email: user.email,
          extension: "-",
          status: user.isActive ? "active" : "inactive",
          leadsCount: user.leadsCount || 0,
          smartFloAccountCreated: user.isTeleAccountCreated,
          contactGroupCreated: user.isContactGroupCreated,
          leadsAddedToGroup: false,
        }));

        console.log("🧩 FORMATTED DATA:", formatted);

        setManagers(formatted);
      } else {
        console.error("❌ API FAILED:", res.error);
      }
    };

    fetchManagers();
  }, []);

  const filtered = managers
    .filter((sm) => {
      const matchSearch =
        sm.name.toLowerCase().includes(search.toLowerCase()) ||
        sm.email.toLowerCase().includes(search.toLowerCase());
      const matchStatus = statusFilter === "all" || sm.status === statusFilter;
      const matchSm = smFilter === "all" || sm.id === smFilter;
      const smLeads = leads.filter((l) => l.salesManagerId === sm.id);
      const matchProject =
        projectFilter === "all" ||
        smLeads.some((l) => l.project === projectFilter);
      const matchUnit =
        unitFilter === "all" ||
        smLeads.some((l) => l.unit.includes(unitFilter));
      return matchSearch && matchStatus && matchSm && matchProject && matchUnit;
    })
    .sort((a, b) => {
      if (sortBy === "name") return a.name.localeCompare(b.name);
      if (sortBy === "status") return a.status.localeCompare(b.status);
      return a.extension.localeCompare(b.extension);
    });

  const handleCreateUserClick = (sm) => {
    setSelectedManager(sm);
    setCreateUserOpen(true);
  };

  const handleCreateGroupClick = (sm) => {
    setSelectedManager(sm);
    setCreateGroupOpen(true);
  };
  const handleAddLeadsClick = (sm) => {
    const latestManager = managers.find((m) => m.id === sm.id);

    console.log("🟢 LATEST MANAGER:", latestManager); // 🔥 CHECK

    setSelectedManager(latestManager); // ✅ USE UPDATED STATE
    setAddLeadsOpen(true);
  };

  const handleImportExcelClick = (sm) => {
    const latestManager = managers.find((m) => m.id === sm.id);
    setSelectedManager(latestManager);
    setImportExcelOpen(true);
  };

  const handleImportExcelSuccess = (smId, importedCount) => {
    setManagers((prev) =>
      prev.map((sm) =>
        sm.id === smId
          ? { ...sm, leadsCount: sm.leadsCount + (importedCount || 0) }
          : sm
      )
    );
  };

  const handleUserCreated = (smId, smartfloUserId) => {
     console.log("🎯 USER CREATED:", smId, smartfloUserId); // ✅ ADD
    setManagers((prev) =>
      prev.map((sm) =>
        sm.id === smId
          ? {
              ...sm,
              smartFloAccountCreated: true,
              smartfloUserId, // 🔥 STORE THIS
            }
          : sm,
      ),
    );
  };
  console.log(managers);

  const handleGroupCreated = (smId, smartfloGroupId) => {
    console.log("🎯 GROUP CREATED:", smId, smartfloGroupId); // ✅ ADD

    setManagers((prev) => {
      const updated = prev.map((sm) =>
        sm.id === smId
          ? {
              ...sm,
              contactGroupCreated: true,
              smartfloGroupId,
              contactGroupCreated: true,
            }
          : sm,
      );

      console.log("🧩 UPDATED MANAGERS:", updated); // ✅ ADD

      return updated;
    });
  };

  // const handleAddLeads = (smId) => {
  //   // 🔥 later we will call API here
  //   console.log("Adding leads for:", smId);

  //   setManagers((prev) =>
  //     prev.map((sm) =>
  //       sm.id === smId ? { ...sm, leadsAddedToGroup: true } : sm,
  //     ),
  //   );
  // };
  const selectSx = {
    minWidth: 140,
    "& .MuiInputBase-root": { fontSize: "0.875rem" },
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 4 }}>
      <Typography sx={{ fontSize: "14px", fontWeight: 600, mt: 1 }}>
        {filtered.length} Sales Managers
      </Typography>

      {/* ─────────  FILTERS ───────── */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1.5,
          flexWrap: "wrap",
        }}
      >
        {/* Filters */}
        {/* {[
          { value: smFilter, set: setSmFilter, label: "All Sales Managers" },
          {
            value: projectFilter,
            set: setProjectFilter,
            label: "All Projects",
          },
          { value: unitFilter, set: setUnitFilter, label: "All Units" },
          { value: statusFilter, set: setStatusFilter, label: "All Status" },
          { value: sortBy, set: setSortBy, label: "Name" },
        ].map((item, i) => (
          <Select
            key={i}
            value={item.value}
            onChange={(e) => item.set(e.target.value)}
            size="small"
            displayEmpty
            sx={{
              height: 36,
              fontSize: "10px",
              borderRadius: "6px",
              bgcolor: "#fff",
              "& .MuiOutlinedInput-notchedOutline": {
                borderColor: "#e5e7eb",
              },
            }}
          >
            <MenuItem value="all">{item.label}</MenuItem>
          </Select>
        ))} */}

        {/* Search — shared SearchBar so the fill, radius, icon size and focus
            ring match Leads. 25rem is the Leads standard width
            (.wrapperSearchBar in Leads.module.css); ml:auto keeps it
            right-aligned in this toolbar exactly as before. */}
        <Box sx={{ ml: "auto", width: "25rem", maxWidth: "100%" }}>
          <SearchBar
            id="smartFloManagerSearch"
            placeholder="Search..."
            search={(e) => setSearch(e.target.value)}
            width="100%"
          />
        </Box>
      </Box>

      {/* ───────── TABLE ───────── */}
      <TableContainer
        component={Paper}
        elevation={0}
        sx={{
          border: "1px solid #e5e7eb",
          borderRadius: "10px",
          overflow: "hidden",
        }}
      >
        <Table size="small">
          {/* HEADER */}
          <TableHead>
            <TableRow sx={{ bgcolor: "#f9fafb" }}>
              {[
                "Name",
                "Email",
                "Extension",
                "Status",
                "SmartFlo",
                // "Lead Handling %",
                "Total Leads",
                "View Recordings",
                "Actions",
              ].map((h) => (
                <TableCell
                  key={h}
                  align={
                    h === "Name"
                      ? "left"
                      : "center" && h === "Email"
                        ? "left"
                        : "center"
                  }
                  sx={{
                    fontSize: "12.5px",
                    fontWeight: 600,
                    color: "#6b7280",
                    py: 1.5,
                  }}
                >
                  {h}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>

          {/* BODY */}
          <TableBody>
            {filtered.map((sm) => {
              const analytics = smAnalytics.find(
                (a) => a.salesManagerId === sm.id,
              );
              const pct = analytics?.leadHandlingPct ?? 0;

              return (
                <TableRow
                  key={sm.id}
                  hover
                  sx={{
                    "& td": {
                      fontSize: "12px",
                      py: 1.4,
                      borderBottom: "1px solid #f1f5f9",
                    },
                  }}
                >
                  {/* Name */}
                  <TableCell sx={{ fontWeight: 500 ,fontFamily:"Inter-Medium",fontSize: "12px!important" }}>{sm.name}</TableCell>

                  {/* Email */}
                  <TableCell sx={{ color: "#6b7280" ,fontFamily:"Inter-Medium",fontSize: "12px!important"}}>{sm.email}</TableCell>

                  {/* Ext */}
                  <TableCell align="center" sx={{ color: "#6b7280" }}>
                    {sm.extension}
                  </TableCell>

                  {/* Status */}
                  <TableCell>
                    <Chip
                      label={sm.status}
                      size="small"
                      sx={{
                        height: 30,
                        bgcolor: sm.status === "active" ? "#e6f4ea" : "#f1f5f9",
                        color: sm.status === "active" ? "#16a34a" : "#6b7280",
                        borderRadius: "20px",
                        textTransform: "capitalize",

                        "& .MuiChip-label": {
                          fontSize: "12px",
                          fontWeight: 500,
                        },
                      }}
                    />
                  </TableCell>

                  {/* SmartFlo */}
                  <TableCell align="left">
                    <Box
                      sx={{
                        display: "flex",
                        gap: 0.5,
                        justifyContent: "center",
                      }}
                    >
                      {sm.smartFloAccountCreated && (
                        <Chip
                          label="Account ✓"
                          size="small"
                          sx={{
                            height: 22,
                            fontSize: "11px",
                            bgcolor: "#e6f4ea",
                            color: "#16a34a",

                            "& .MuiChip-label": {
                              fontSize: "12px",
                              fontWeight: 500,
                            },
                          }}
                        />
                      )}
                      {sm.contactGroupCreated && (
                        <Chip
                          label="Group ✓"
                          size="small"
                          sx={{
                            height: 22,
                            fontSize: "5px",
                            bgcolor: "#e8f0fe",
                            color: "#2563eb",

                            "& .MuiChip-label": {
                              fontSize: "12px",
                              fontWeight: 500,
                            },
                          }}
                        />
                      )}
                      {sm.leadsAddedToGroup && (
                        <Chip
                          label="Leads ✓"
                          size="small"
                          sx={{
                            bgcolor: "#dcfce7",
                            color: "#804015",
                            "& .MuiChip-label": {
                              fontSize: "12px",
                              fontWeight: 500,
                            },
                          }}
                        />
                      )}
                      {!sm.smartFloAccountCreated &&
                        !sm.contactGroupCreated && (
                          <Typography
                            sx={{
                              fontSize: "12px !important",
                              color: "#9ca3af",
                            }}
                          >
                            Not set up
                          </Typography>
                        )}
                    </Box>
                  </TableCell>

                  {/*lead handling % */}
                  {/* <TableCell>
                    <Typography
                      sx={{
                        color: "#f59e0b",
                        fontWeight: 600,
                        justifyContent: "center",
                        display: "flex",
                        fontSize: "13px !important",
                      }}
                    >
                      {pct}%
                    </Typography>
                  </TableCell> */}

                  {/* Total */}
                  <TableCell sx={{ color: "#6b7280" }}>
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "flex-end",
                        gap: 2,
                        fontSize: "13px !important",
                      }}
                    >
                      {sm.leadsCount}
                      {/* <IconButton
                        size="small"
                        onClick={() => onViewLeads(sm.id, sm.name)}
                      >
                        <Visibility sx={{ fontSize: 18 }} />
                      </IconButton> */}
                    </Box>
                  </TableCell>
                  {/* View Recording  */}

                  <TableCell sx={{ color: "#6b7280" }}>
                    <Box
                      sx={{ display: "flex", justifyContent: "center", gap: 1 }}
                    >
                      <IconButton
                        size="small"
                        onClick={() => onViewRecordings(sm.id)}
                      >
                        <Headphones sx={{ fontSize: 18 }} />
                      </IconButton>
                    </Box>
                  </TableCell>

                  {/* Actions */}
                  <TableCell>
  <Box
    sx={{
      display: "flex",
      justifyContent: "center",
      gap: 0.5,
    }}
  >
    {/* ✅ SHOW CHIP + IMPORT BUTTON WHEN BOTH CREATED */}
    {sm.smartFloAccountCreated && sm.contactGroupCreated && (
      <>
        {/* <Chip
          label=" Set Up Completed"
          size="small"
          sx={{
            bgcolor: "#e6f4ea",
            color: "#16a34a",
            fontFamily: "Inter-Medium",
            fontSize: "12px !important",
            fontWeight: 500,
            borderRadius: "20px",
          }}
        /> */}
        <Button
          size="small"
          variant="outlined"
          onClick={() => handleImportExcelClick(sm)}
          sx={{
            fontSize: "11px !important",
            textTransform: "none",
            borderRadius: "6px",
            px: 1.5,
            py: 0.5,
            borderColor: "#2563eb",
            color: "#2563eb",
            whiteSpace: "nowrap",
            "&:hover": { bgcolor: "#eff6ff", borderColor: "#1d4ed8" },
          }}
        >
          Import Leads From Excel
        </Button>
      </>
    )}

    {/* CREATE ACCOUNT */}
    {!sm.smartFloAccountCreated && (
      <Button
        size="small"
        variant="contained"
        onClick={() => handleCreateUserClick(sm)}
        sx={{
          fontSize: "12px!important",
          textTransform: "none",
          bgcolor: "#2563eb",
        }}
      >
        + Create Account
      </Button>
    )}

    {/* CREATE GROUP */}
    {sm.smartFloAccountCreated && !sm.contactGroupCreated && (
      <Button
        size="small"
        variant="outlined"
        onClick={() => handleCreateGroupClick(sm)}
        sx={{
          fontSize: "12px !important",
          textTransform: "none",
          borderRadius: "6px",
          px: 1.5,
          py: 0.5,
        }}
      >
        Create Group
      </Button>
    )}
  </Box>
</TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
      {/* <AddLeadsModal
        open={addLeadsOpen}
        onClose={() => setAddLeadsOpen(false)}
        manager={selectedManager}
        onSuccess={(smId) => {
          setManagers((prev) =>
            prev.map((sm) =>
              sm.id === smId ? { ...sm, leadsAddedToGroup: true } : sm,
            ),
          );
        }}
      /> */}
      <CreateUserModal
        open={createUserOpen}
        onClose={() => setCreateUserOpen(false)}
        manager={selectedManager}
        onSuccess={handleUserCreated}
      />
      <CreateGroupModal
        open={createGroupOpen}
        onClose={() => setCreateGroupOpen(false)}
        manager={selectedManager}
        onSuccess={handleGroupCreated}
      />
      <ImportLeadsExcelModal
        open={importExcelOpen}
        onClose={() => setImportExcelOpen(false)}
        manager={selectedManager}
        onSuccess={handleImportExcelSuccess}
      />
    </Box>
  );
}
